import { AlertCircle, X } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

interface ExtendedError extends Error {
  errorType?: string
}

interface ErrorAlertProps {
  error: Error | string
  onDismiss?: () => void
  className?: string
}

export function ErrorAlert({ error, onDismiss, className }: ErrorAlertProps) {
  const errorMessage = typeof error === 'string' ? error : error.message
  const errorType = (error as ExtendedError)?.errorType || 'Error'
  
  // Map error types to user-friendly messages
  const getErrorTitle = (type: string) => {
    switch (type) {
      case 'AccessDenied':
        return 'Access Denied'
      case 'DataNotFound':
        return 'Data Not Found'
      case 'ValidationError':
        return 'Invalid Input'
      case 'CalculationError':
        return 'Calculation Failed'
      case 'ApiError':
        return 'API Error'
      default:
        return 'Error'
    }
  }

  const getErrorDescription = (type: string, message: string) => {
    switch (type) {
      case 'AccessDenied':
        return 'You do not have permission to access the required data. Please contact your administrator.'
      case 'DataNotFound':
        return 'The required data file could not be found. Please try again later or contact support.'
      case 'ValidationError':
        return `Please check your input: ${message}`
      case 'CalculationError':
        return 'An error occurred during the calculation. Please try again or contact support.'
      case 'ApiError':
        return message
      default:
        return message
    }
  }

  return (
    <Alert variant="destructive" className={className}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <h4 className="font-medium">{getErrorTitle(errorType)}</h4>
            <AlertDescription className="mt-1">
              {getErrorDescription(errorType, errorMessage)}
            </AlertDescription>
          </div>
        </div>
        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Alert>
  )
}
